import React, {useState} from 'react';
import {
  DimensionValue,
  FlatList,
  LayoutChangeEvent,
  ListRenderItem,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';

import config from '../../config';
import {DEFAULT_CAROUSEL_NUM_ITEMS} from '../../constants/GlobalConstants';
import {ScreenTypes, useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import theme from '../../style/theme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {visibleScrollbarProps} from '../../utils/webScrollbar';
import {ProductTileCarousel} from '../ProductTile';
import {TILE_DIMENSIONS} from '../ProductTile/styles';
import Text from '../themed/Text';

type PromotionProductListingProps = {
  items: OrderLine[];
};

/**
 * DIS-1458: Promotion Detail linked-products listing.
 * Collapsed (default) shows a horizontal carousel capped at
 * DEFAULT_CAROUSEL_NUM_ITEMS tiles with a trailing "See all" card when the
 * promotion has more products. Expanding swaps the carousel for a responsive,
 * non-scrolling inline grid of ALL products (it lives inside the screen's
 * single ScrollView, so it must not nest-scroll).
 */
const PromotionProductListing = ({items}: PromotionProductListingProps) => {
  const [expanded, setExpanded] = useState(false);
  // Measured height of a product tile — applied to the "See all" card so it
  // matches the tiles exactly (tiles are a fixed/uniform height, so measuring
  // the first one is enough) and reads as one unified row.
  const [tileHeight, setTileHeight] = useState<number>();
  const {screenType, isWebDesktopScreen} = useWebScreenDimensions();

  const cap = DEFAULT_CAROUSEL_NUM_ITEMS;
  const total = items.length;
  const hasOverflow = total > cap;

  // Promotion has linked barcodes but the current store carries none of them
  // (the parent only mounts this once barcodes exist, so total === 0 means the
  // store has zero matching products). Show a centred, brand-fonted notice in
  // place of the product list rather than an empty section.
  if (total === 0) {
    return (
      <View testID="promotion-product-listing" style={styles.emptyContainer}>
        <Text testID="promotion-products-empty" style={styles.emptyMessage}>
          Not available at this store right now.
        </Text>
      </View>
    );
  }

  let numColumns = 2;
  switch (screenType) {
    case ScreenTypes.desktop:
      numColumns = 4;
      break;
    case ScreenTypes.desktopLarge:
      numColumns = 6;
      break;
  }

  // Fixed fractional width per grid cell so rows wrap cleanly and a lone last-row
  // tile stays column-width. The expanded grid is a flex-wrap View (not a FlatList):
  // it lives inside the screen's vertical ScrollView, is fully in memory and bounded,
  // so a plain wrap avoids nested-VirtualizedList warnings and needs no virtualization.
  const gridItemWidth: DimensionValue = `${100 / numColumns}%`;

  const expand = () => setExpanded(true);
  const collapse = () => setExpanded(false);

  const countLabel = `${total} ${total === 1 ? 'PRODUCT' : 'PRODUCTS'} IN THIS PROMOTION`;

  const handleTileLayout = (e: LayoutChangeEvent) => {
    setTileHeight(e.nativeEvent.layout.height);
  };

  const renderCarouselItem: ListRenderItem<OrderLine> = ({item, index}) => (
    <View onLayout={index === 0 ? handleTileLayout : undefined}>
      <ProductTileCarousel item={item} testContext="carousel" />
    </View>
  );

  return (
    <View testID="promotion-product-listing">
      <View style={styles.header}>
        <Text style={styles.countLabel}>{countLabel}</Text>
        {hasOverflow ? (
          expanded ? (
            <Pressable
              testID="promotion-products-collapse"
              onPress={collapse}
              accessibilityRole="button"
              accessibilityLabel="Collapse products">
              <Text style={styles.headerLink}>COLLAPSE</Text>
            </Pressable>
          ) : (
            <Pressable
              testID="promotion-products-expand"
              onPress={expand}
              accessibilityRole="button"
              accessibilityLabel={`Expand all ${total} products`}>
              <Text style={styles.headerLink}>EXPAND ALL</Text>
            </Pressable>
          )
        ) : null}
      </View>

      {expanded ? (
        <View testID="promotion-products-grid" style={styles.grid}>
          {/* Renders every linked product with no cap/virtualization: the set is
              Airtable-curated via promotion.barcodes and bounded small in practice,
              and it must render inline inside the screen's ScrollView. */}
          {items.map(item => (
            <View
              key={String(item.product_id)}
              style={[styles.tileWrapper, {width: gridItemWidth}]}>
              <ProductTileCarousel item={item} fullWidth testContext="grid" />
            </View>
          ))}
        </View>
      ) : (
        <FlatList
          testID="promotion-products-carousel"
          horizontal
          style={styles.carouselList}
          contentContainerStyle={[
            styles.carouselContent,
            isWebDesktopScreen && styles.carouselContentWithScrollbar,
          ]}
          data={items.slice(0, cap)}
          keyExtractor={item => String(item.product_id)}
          renderItem={renderCarouselItem}
          ItemSeparatorComponent={Spacer}
          // DIS-1555: desktop-web mouse users have no horizontal-scroll gesture, so the
          // hidden indicator left the trailing "See all" card unreachable. The helper
          // forces the bar to stay visible — macOS overlay scrollbars auto-hide when
          // idle, so the flag alone still leaves no visible affordance.
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          {...visibleScrollbarProps(isWebDesktopScreen)}
          ListFooterComponent={
            hasOverflow ? (
              <Pressable
                testID="promotion-products-see-all"
                onPress={expand}
                accessibilityRole="button"
                accessibilityLabel={`See all ${total} products`}
                style={[styles.seeAllCard, tileHeight != null && {height: tileHeight}]}>
                <Text style={styles.seeAllCount}>{`+${total - cap}`}</Text>
                <Text style={styles.seeAllLabel}>{`SEE ALL ${total} PRODUCTS`}</Text>
                <Text style={styles.seeAllChevron}>{'›'}</Text>
              </Pressable>
            ) : null
          }
        />
      )}
    </View>
  );
};

function Spacer() {
  return <View style={styles.spacer} />;
}

// Section-accent scheme (header count, 2px border, expand/collapse links), keyed by
// tenant so the colour choice can change without renaming anything:
// Thirsty Camel + Harry Brown use their brand black (text.heading.primary);
// Bottlemart + Sip'n'Save use the app-wide link blue.
const ACCENT =
  config.BASE_TENANT === 'TC' || config.BASE_TENANT === 'HB'
    ? theme.colour.text.heading.primary
    : theme.colour.text.link.primary;
// "See all" card is a CTA, so it uses the brand button token pair (matches Add-to-cart).
const CTA_BG = theme.colour.background.button.primary;
const CTA_ON = theme.colour.background.button.contrastOnPrimary;

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 32,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: ACCENT,
  },
  countLabel: {
    flexShrink: 1,
    marginRight: 12,
    color: ACCENT,
    fontSize: 14,
    fontWeight: 'bold',
    letterSpacing: 0.8,
  },
  headerLink: {
    color: ACCENT,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 0.5,
    textDecorationLine: 'underline',
  },
  carouselList: {
    marginTop: 16,
  },
  carouselContent: {
    paddingBottom: 6,
  },
  // The always-visible desktop-web scrollbar sits inside the scroll box, so the
  // content needs to clear its 10px track instead of being overlapped by it.
  carouselContentWithScrollbar: {
    paddingBottom: 16,
  },
  spacer: {
    width: 8,
  },
  seeAllCard: {
    width: TILE_DIMENSIONS.width,
    marginLeft: 8,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 10,
    backgroundColor: CTA_BG,
  },
  seeAllCount: {
    color: CTA_ON,
    fontSize: 32,
    fontWeight: 'bold',
  },
  seeAllLabel: {
    marginTop: 8,
    color: CTA_ON,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  seeAllChevron: {
    marginTop: 2,
    color: CTA_ON,
    fontSize: 22,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
    paddingHorizontal: 4,
  },
  tileWrapper: {
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  emptyContainer: {
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyMessage: {
    color: theme.colour.text.content.primary,
    fontSize: 15,
    textAlign: 'center',
  },
});

export default PromotionProductListing;
